import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AcceptQuoteScheduleDialog } from './AcceptQuoteScheduleDialog';
import { acceptQuoteErrorMessage } from './acceptQuoteError';
import { useAcceptQuote, type AcceptQuoteData } from '@/hooks/useBookings';
import { useWorkOrder } from '@/hooks/useWorkOrders';
import { useNotify } from '@/stores/notificationStore';

interface QuoteAcceptFlowProps {
  /** The quote to accept; `null` renders nothing. */
  quoteId: string | null;
  /** A slot picked outside the work order, e.g. a calendar cell (#2653). */
  initialScheduledAt?: Date | undefined;
  initialMechanicId?: string | undefined;
  onClose: () => void;
  /** After a successful acceptance; the work order id equals the quote id. */
  onAccepted: (workOrderId: string, workOrderNumber: string | null) => void;
}

/**
 * "Accept and schedule" for a quote identified only by id (#2653).
 *
 * The detail page already holds the loaded work order and drives
 * AcceptQuoteScheduleDialog itself. The calendar and the create wizard know
 * just the id — from a quote picker or a plate match — so this loads the
 * detail, opens the same dialog with the picked slot prefilled, runs the
 * accept mutation and reports the same 403/409 messages. One flow, three
 * entry points, so a quote is accepted the same way everywhere.
 */
export function QuoteAcceptFlow({
  quoteId,
  initialScheduledAt,
  initialMechanicId,
  onClose,
  onAccepted,
}: QuoteAcceptFlowProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { data: workOrder, isError } = useWorkOrder(quoteId ?? undefined);
  const acceptQuote = useAcceptQuote();

  // The quote may have been handled meanwhile (accepted or rejected in another
  // tab): the dialog must not offer to accept something that is no longer a
  // quote, so say so and step back instead of opening.
  const noLongerQuote = !!workOrder && workOrder.status !== 'quote';
  // The status event can arrive before our HTTP response (or its replay).
  // Keep the observer mounted until that request reports its own outcome.
  const acceptedByThisFlow = acceptQuote.isSuccess && acceptQuote.variables?.id === quoteId;
  useEffect(() => {
    if (!quoteId || acceptQuote.isPending || acceptedByThisFlow) return;
    if (isError) {
      notify.error(tc('error'), t('detail.notFound'));
      onClose();
    } else if (noLongerQuote) {
      notify.error(tc('error'), t('acceptQuote.noLongerQuote'));
      onClose();
    }
  }, [
    quoteId,
    isError,
    noLongerQuote,
    acceptQuote.isPending,
    acceptedByThisFlow,
    notify,
    onClose,
    t,
    tc,
  ]);

  if (!quoteId || !workOrder || (noLongerQuote && !acceptQuote.isPending)) return null;

  const handleConfirm = (data: AcceptQuoteData, expectedUpdatedAt: string) => {
    acceptQuote.mutate(
      { id: workOrder.id, data, expectedUpdatedAt },
      {
        onSuccess: () => onAccepted(workOrder.id, workOrder.workOrderNumber ?? null),
        onError: (error) => notify.error(tc('error'), acceptQuoteErrorMessage(error, t)),
      }
    );
  };

  return (
    <AcceptQuoteScheduleDialog
      open
      workOrder={workOrder}
      onConfirm={handleConfirm}
      onCancel={onClose}
      loading={acceptQuote.isPending}
      initialScheduledAt={initialScheduledAt}
      initialMechanicId={initialMechanicId}
    />
  );
}
